import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import SelectFormInput from "src/components/form/inputs/SelectFormInput/SelectFormInput";
import { GroupItemsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { GroupSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupItemCategoryActions from "../../../actions/groupItemCategory";
import * as GroupItemActions from "../../../actions/groupItems";
import * as GroupItemTemplateActions from "../../../actions/groupItemTemplate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as GroupItemsPageActions from "../../../actions/pages/groupItemsPage";
import { InlineLoading, Modal, NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { ITmdState } from "../../../reducers/index";
import { GroupItemCreateVersion } from "../../index";

import "bootstrap/dist/js/bootstrap.bundle.min";

import "./GroupItemsPage.scss";

interface GroupItemsPageProps {
    group: Dtos.Group;
    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;
    groupItemCategories: Dtos.GroupItemCategory[];
    loadingGroupItemCategories: boolean;
    loadGroupItemCategoriesSuccess: boolean;
    loadGroupItemCategoriesFailure: boolean;
    loadGroupItemCategoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    groupItemTemplates: Dtos.GroupItemTemplate[];
    loadingGroupItemTemplates: boolean;
    loadGroupItemTemplatesSuccess: boolean;
    loadGroupItemTemplatesFailure: boolean;
    loadGroupItemTemplatesErrorCode: string;
    savingGroupItem: boolean;
    saveGroupItemSuccess: boolean;
    createVersionModalOpen: boolean;
    createVersionGroupItem: Dtos.GroupItem;
    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;
    clearGroupItemCategories: GroupItemCategoryActions.IGroupItemCategoriesClearActionCreator;
    loadGroupItemCategories: GroupItemCategoryActions.IGroupItemCategoriesLoadByGroupIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearGroupItemTemplates: GroupItemTemplateActions.IGroupItemTemplateClearActionCreator;
    loadGroupItemTemplates: GroupItemTemplateActions.IGroupItemTemplateLoadActionCreator;
    saveGroupItem: GroupItemActions.IGroupItemSaveActionCreator;
    setEditModelViewState: GroupItemsPageActions.IGroupItemsPageSetEditModalViewStateActionCreator;
    setCreateVersionModelViewState: GroupItemsPageActions.IGroupItemsPageSetCreateVersionModalViewStateActionCreator;
}

const GroupItemsPage: FC<GroupItemsPageProps> = ({
    group,
    loadingGroup,
    loadGroupSuccess,
    loadGroupFailure,
    loadGroupErrorCode,
    groupItemCategories,
    loadingGroupItemCategories,
    loadGroupItemCategoriesSuccess,
    loadGroupItemCategoriesFailure,
    loadGroupItemCategoriesErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    groupItemTemplates,
    loadingGroupItemTemplates,
    loadGroupItemTemplatesSuccess,
    loadGroupItemTemplatesFailure,
    loadGroupItemTemplatesErrorCode,
    savingGroupItem,
    saveGroupItemSuccess,
    createVersionModalOpen,
    createVersionGroupItem,
    clearGroup,
    loadGroup,
    clearGroupItemCategories,
    loadGroupItemCategories,
    setBreadcrumbs,
    setDrawerItems,
    clearGroupItemTemplates,
    loadGroupItemTemplates,
    saveGroupItem,
    setEditModelViewState,
    setCreateVersionModelViewState
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addGroupItemId, setAddGroupItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showGroupItems =
        !loadingGroup &&
        loadGroupSuccess &&
        !loadingGroupItemTemplates &&
        loadGroupItemTemplatesSuccess &&
        !loadingGroupItemCategories &&
        loadGroupItemCategoriesSuccess &&
        !creatingTree;


    // Load Group, Items & Templates
    useEffect(() => {
        if (groupIdNumber && !group && !loadingGroup) {
            setBreadcrumbs(GroupItemsBreadcrumbs(groupIdNumber));
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));

            loadGroup(groupIdNumber);
            loadGroupItemTemplates(groupIdNumber);
            loadGroupItemCategories(groupIdNumber);
        }
    }, [
        groupIdNumber,
        permissions,
        group,
        setBreadcrumbs,
        setDrawerItems,
        loadGroup,
        GroupItemsBreadcrumbs,
        GroupSubMenu,
        loadGroupItemTemplates,
        loadingGroup,
        loadingGroupItemTemplates,
        loadGroupItemCategories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
        }
        if (loadGroupItemTemplatesFailure) {
            navigate(`/error/${loadGroupItemTemplatesErrorCode}`);
        }
        if (loadGroupItemCategoriesFailure) {
            navigate(`/error/${loadGroupItemCategoriesErrorCode}`);
        }
    }, [
        loadGroupFailure,
        loadGroupErrorCode,
        loadGroupItemTemplatesFailure,
        loadGroupItemTemplatesErrorCode,
        loadGroupItemCategoriesFailure,
        loadGroupItemCategoriesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (groupIdNumber && group?.name) {
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));
            setBreadcrumbs(GroupItemsBreadcrumbs(groupIdNumber, group.name));
        }
    }, [
        groupIdNumber,
        group,
        permissions,
        GroupSubMenu,
        GroupItemsBreadcrumbs,
        setDrawerItems,
        setDrawerItems,
    ]);

    // Cleanup effect to clear the group item on component unmount
    useEffect(() => {
        return () => {
            clearGroup();
            clearGroupItemTemplates();
            clearGroupItemCategories();
        };
    }, [clearGroup, clearGroupItemTemplates, clearGroupItemCategories]);

    // Reload group & group items after saving group item
    useEffect(() => {
        if (!savingGroupItem && saveGroupItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadGroup(groupIdNumber);
            loadGroupItemTemplates(groupIdNumber);
            loadGroupItemCategories(groupIdNumber);
            setInitalizedTreeData(false);
        }
    }, [
        savingGroupItem,
        saveGroupItemSuccess,
        groupIdNumber,
        loadGroup,
        loadGroup,
        loadGroupItemTemplates,
        loadGroupItemCategories,
        setInitalizedTreeData,
    ]);

    // Refresh tree
    useEffect(() => {
        if (groupItemCategories) {
            setInitalizedTreeData(false);
        }
    }, [groupItemCategories, setInitalizedTreeData]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddGroupItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addGroupItemId || !groupIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the group item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }
        let template = groupItemTemplates.find((x) => x.id === addGroupItemId);
        let groupItem: Dtos.GroupItem = {
            groupId: groupIdNumber,
            groupItemTemplateId: addGroupItemId,
            version: template?.initialVersion ?? null,
            active: true,
        } as Dtos.GroupItem;
        await saveGroupItem(groupItem, null);

        setIsOpen(false);
        setAddGroupItemId(undefined);
    };

    // Create tree
    useEffect(() => {
        if (groupItemCategories && !initalizedTreeData) {
            setCreatingTree(true);

            // If no group items return
            if (groupItemCategories?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                groupItemSummaries: Dtos.GroupItemSummary[]
            ) => {
                let children = [];
                const groupedSummaries = Object.values<Dtos.GroupItemSummary>(
                    groupItemSummaries.reduce((acc, item) => {
                        const key = `${item.groupId}-${item.groupItemTemplateId}`;

                        if (!acc[key] || new Date(item.dateEntered) > new Date(acc[key].dateEntered)) {
                            acc[key] = item;
                        }

                        return acc;
                    }, {})
                );

                children =
                    groupedSummaries
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FILE} />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.version && (
                                            <button
                                                disabled={
                                                    !AuthenticationHelper.hasPermission(
                                                        permissions,
                                                        Dtos.Permission.GroupItemUpdate,
                                                        null,
                                                        null
                                                    )
                                                }
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm btn-row-version"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (y?.collectionInstanceId) {
                                                        setEditModelViewState(
                                                            true,
                                                            y.collectionInstanceId,
                                                            y?.name,
                                                            y?.description
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>{y.version}</span>
                                            </button>
                                        )}
                                        {y?.groupItemSummaryVersions?.map(
                                            (item, index) => (
                                                <button
                                                    key={`version-button-${item?.version}-${index}`}
                                                    className="btn btn-secondary btn-sm btn-row-version"
                                                    type="button"
                                                    disabled={
                                                        !AuthenticationHelper.hasPermission(
                                                            permissions,
                                                            Dtos.Permission.GroupItemUpdate,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item?.collectionInstanceId) {
                                                            setEditModelViewState(
                                                                true,
                                                                item.collectionInstanceId,
                                                                y?.name,
                                                                y?.description
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span>{item.version}</span>
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="px-1 d-flex align-items-start justify-content-end">
                                        {y?.allowVersions && (
                                            <button
                                                disabled={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const groupItems = groupItemCategories?.flatMap(x => x.groupItems) ?? [];
                                                    setCreateVersionModelViewState(
                                                        true,
                                                        groupItems.find((x) => x.id == y.id)
                                                    );
                                                }}
                                                type="button"
                                                className="btn btn-outline-secondary mr-2 btn-sm"
                                            >
                                                <span>New version</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Version</strong>
                                </div>
                                <div className="px-1"></div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...groupItemCategories]
                    ?.sort((a, b) => a?.groupItemTemplateCategory?.rank - b?.groupItemTemplateCategory?.rank)
                    ?.map((x) => ({
                        id: `category-${x?.groupItemTemplateCategory?.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.groupItemTemplateCategory?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        { [...new Set(x?.groupItems?.map((x) => x.groupItemTemplateId)),]?.length }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.groupItemSummaries),],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        groupItemCategories,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id.includes("item")) {
                    const groupItems = groupItemCategories?.flatMap(x => x.groupItems) ?? [];
                    const selectedGroupItem: Dtos.GroupItem = groupItems?.find((x) => x.id?.toString() == id.replace("item-", ""));
                    setEditModelViewState(
                        true,
                        selectedGroupItem?.collectionInstanceId,
                        selectedGroupItem?.groupItemTemplate?.name,
                        selectedGroupItem?.groupItemTemplate?.description
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const renderCreateVersionModal = useMemo(() => {
        if (group && !loadingGroup) {
            return (
                <Modal
                    open={createVersionModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setCreateVersionModelViewState(false, null);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {setModalTitle(undefined, "Create Version")}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                        <GroupItemCreateVersion
                            groupId={groupIdNumber}
                            currentGroupItem={createVersionGroupItem}
                            onClose={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
    }, [
        groupIdNumber,
        loadingGroup,
        createVersionModalOpen,
        setCreateVersionModelViewState,
        createVersionGroupItem,
    ]);

    const renderItemModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.GroupItemUpdate,
            undefined,
            undefined
        );
        const formDisabled = !hasPermission;

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setEditModelViewState(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small>
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadGroupItemCategories(groupIdNumber);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        groupIdNumber,
        setEditModelViewState,
        loadGroupItemCategories,
    ]);

    const selectOptions = useMemo(() => {
        if (!groupItemTemplates || !group) return [];

        const groupItems = groupItemCategories?.flatMap(x => x.groupItems) ?? [];
        const currentIds = [...new Set(groupItems?.map((x) => x.groupItemTemplateId))];

        var options = groupItemTemplates
            ?.filter((x) => !currentIds.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.initialVersion ? `${x?.name} | ${x.initialVersion}` : x?.name }));

        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [groupItemTemplates, groupItemCategories]);

    return (
        <RestrictedLayout
            loading={loadingGroup || loadingGroupItemTemplates || loadingGroupItemCategories}
            permission={Dtos.Permission.GroupItemView}
        >
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Group Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupItemCreate,
                            groupIdNumber,
                            null
                        ) && (
                            <>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add Item Template
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span className="sr-only">
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <div className="dropdown-menu dropdown-menu-right">
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/group/itemtemplates/${groupIdNumber}/template/create`)
                                            }
                                        >
                                            Create Item Template
                                        </a>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/group/itemtemplates/${groupIdNumber}`)
                                            }
                                        >
                                            Edit Item Templates
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() => setIsOpen(true)}
                                        >
                                            Add Item Template
                                        </a>
                                    </div>
                                </div>
                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Lg}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Group Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions?.length === 0 ? (
                                                    <>
                                                        <span>
                                                            No <em>Group Item Templates</em> are available for <strong>{group?.name}</strong>.
                                                        </span>
                                                        <br />
                                                        <button
                                                            className="btn btn-primary mr-1 mt-4"
                                                            onClick={() =>
                                                                navigate(`/settings/group/itemtemplates/${groupIdNumber}/template/create`)
                                                            }
                                                        >
                                                            Create Item Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingGroupItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addGroupItemId"}
                                                        id={"addGroupItemId"}
                                                        placeholder="Group Item"
                                                        onChange={function (value: any, _options?: any): any {
                                                            setAddGroupItemId(
                                                                value != undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addGroupItemId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                {selectOptions?.length > 0 && (
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={savingGroupItem}
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </div>
                </div>
                {!showGroupItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showGroupItems ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                        {renderCreateVersionModal}
                    </>
                ) : null}
            </div>
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<GroupItemsPageProps> => {
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let groupItemCategories: Dtos.GroupItemCategory[] | undefined = (state.groupItemCategory.formData instanceof Array)
            ? state.groupItemCategory.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        group: group!,
        loadingGroup: state.groups.loadState && state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess: state.groups.loadState && state.groups.loadState.status == RequestState.Success,
        loadGroupFailure: state.groups.loadState && state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,
        groupItemCategories: groupItemCategories!,
        loadingGroupItemCategories: state.groupItemCategory.loadState && state.groupItemCategory.loadState.status == RequestState.Pending,
        loadGroupItemCategoriesSuccess: state.groupItemCategory.loadState && state.groupItemCategory.loadState.status == RequestState.Success,
        loadGroupItemCategoriesFailure: state.groupItemCategory.loadState && state.groupItemCategory.loadState.status == RequestState.Failure,
        loadGroupItemCategoriesErrorCode: state.trialItemCategory.loadState.errorCode,
        permissions: permissions,
        collectionInstanceModalOpen: state.groupItemsPage.editModalOpen,
        collectionInstanceId: state.groupItemsPage.collectionInstanceId,
        modalTitle: state.groupItemsPage.name,
        modalDescription: state.groupItemsPage.description,
        groupItemTemplates: state.groupItemTemplate.formFilteredData!,
        loadingGroupItemTemplates: state.groupItemTemplate.loadState && state.groupItemTemplate.loadState.status == RequestState.Pending,
        loadGroupItemTemplatesSuccess: state.groupItemTemplate.loadState && state.groupItemTemplate.loadState.status == RequestState.Success,
        loadGroupItemTemplatesFailure: state.groupItemTemplate.loadState && state.groupItemTemplate.loadState.status == RequestState.Failure,
        loadGroupItemTemplatesErrorCode: state.groupItemTemplate.loadState.errorCode,
        savingGroupItem: state.groupItems.saveState && state.groupItems.saveState.status == RequestState.Pending,
        saveGroupItemSuccess: state.groupItems.saveState && state.groupItems.saveState.status == RequestState.Success,
        createVersionModalOpen: state.groupItemsPage.createVersionModalOpen,
        createVersionGroupItem: state.groupItemsPage.groupItem,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupItemsPageProps> => {
    return {
        loadGroup: bindActionCreators(GroupActions.LoadGroupById, dispatch),
        clearGroup: bindActionCreators(GroupActions.Clear, dispatch),
        loadGroupItemTemplates: bindActionCreators(GroupItemTemplateActions.LoadGroupItemTemplate, dispatch),
        clearGroupItemTemplates: bindActionCreators(GroupItemTemplateActions.Clear, dispatch),
        saveGroupItem: bindActionCreators(GroupItemActions.SaveGroupItem, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        setEditModelViewState: bindActionCreators(GroupItemsPageActions.setEditModalViewState, dispatch),
        setCreateVersionModelViewState: bindActionCreators(GroupItemsPageActions.setCreateVersionModalViewState, dispatch),
        loadGroupItemCategories: bindActionCreators(GroupItemCategoryActions.LoadGroupItemCategoriesByGroupId, dispatch),
        clearGroupItemCategories: bindActionCreators(GroupItemCategoryActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupItemsPage);
